import { useState, type ReactElement } from 'react';
import { NavLink, Link } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { useTheme } from '../lib/useTheme';
import RecipeaseLogo from './RecipeaseLogo';
import { MenuIcon, XIcon } from './icons';

/**
 * The bar from the design system's mocks: the lockup, the four places a cook
 * goes, and two text buttons. The theme control is a word — the theme in use,
 * as the mock shows it — rather than an icon to decode.
 */
function Navbar(): ReactElement {
	const { isLoggedin, logout } = useAuth();
	const { theme, toggleTheme } = useTheme();
	const [open, setOpen] = useState(false);

	function close(): void {
		setOpen(false);
	}

	function handleLogout(): void {
		close();
		logout();
	}

	const navLinkClass = ({ isActive }: { isActive: boolean }): string =>
		isActive ? 'nav-link is-active' : 'nav-link';

	return (
		<header className='site-header'>
			<div className='site-header__bar container'>
				<Link className='brand-link' to='/' onClick={close} aria-label='Recipease home'>
					<RecipeaseLogo />
				</Link>

				<button
					type='button'
					className='btn btn--icon nav-toggle'
					aria-label='Toggle navigation'
					aria-expanded={open}
					aria-controls='site-nav'
					onClick={() => setOpen((v) => !v)}
				>
					{open ? <XIcon /> : <MenuIcon />}
				</button>

				<div id='site-nav' className={open ? 'site-nav is-open' : 'site-nav'}>
					<nav className='site-nav__links' aria-label='Main'>
						<NavLink className={navLinkClass} to='/recipes' onClick={close}>Recipes</NavLink>
						{isLoggedin && (
							<>
								<NavLink className={navLinkClass} to='/pantry' onClick={close}>Pantry</NavLink>
								<NavLink className={navLinkClass} to='/shopping-list' onClick={close}>Shopping list</NavLink>
								<NavLink className={navLinkClass} to='/profile' onClick={close}>Profile</NavLink>
							</>
						)}
					</nav>

					<div className='site-nav__actions'>
						<button
							type='button'
							className='btn'
							onClick={toggleTheme}
							aria-label={theme === 'dark' ? 'Dark theme, switch to light' : 'Light theme, switch to dark'}
						>
							{theme === 'dark' ? 'Dark' : 'Light'}
						</button>
						{isLoggedin ? (
							<button type='button' className='btn' onClick={handleLogout}>Log out</button>
						) : (
							<>
								<Link className='btn' to='/login' onClick={close}>Log in</Link>
								<Link className='btn btn--primary' to='/signup' onClick={close}>Sign up</Link>
							</>
						)}
					</div>
				</div>
			</div>
		</header>
	);
}

export default Navbar;
